import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_BASE; // ✅ unified base

const UploadProfilePicture = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const navigate = useNavigate();
  const email = localStorage.getItem("userEmail");

  /* =========================================================
     🧹 Clean up preview object URLs
  ========================================================= */
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  /* =========================================================
     📤 Handle Upload
  ========================================================= */
  const handleUpload = async () => {
    if (!file) return alert("⚠️ Please select a file.");

    if (!file.type.startsWith("image/")) {
      alert("❌ Please upload an image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("❌ Image too large. Max 5 MB.");
      return;
    }

    const formData = new FormData();
    formData.append("profilePicture", file);
    formData.append("email", email);

    try {
      await axios.post(`${API_BASE}/api/customers/upload-profile-picture`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      navigate("/terms");
    } catch (err) {
      console.error("❌ Upload error:", err);
      alert("❌ Failed to upload profile picture.");
    }
  };

  /* =========================================================
     ⏭ Handle Skip
  ========================================================= */
  const handleSkip = async () => {
    try {
      await axios.post(`${API_BASE}/api/customers/update-profile`, {
        email,
        profilePictureSkipped: true,
      });
      navigate("/terms");
    } catch (err) {
      console.error("❌ Skip error:", err);
      alert("❌ Failed to skip profile picture.");
    }
  };

  /* =========================================================
     🎨 UI
  ========================================================= */
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-[#eef2ff] to-[#fff1f9] relative px-4">
      {/* ⬆ Skip link */}
      <span
        onClick={handleSkip}
        className="absolute top-4 right-6 text-sm text-blue-600 font-semibold hover:underline cursor-pointer"
      >
        Skip for now
      </span>

      {/* 🧾 Upload Card */}
      <div className="bg-white shadow-xl rounded-2xl p-8 w-full max-w-sm text-center mt-10">
        <h2 className="text-xl font-semibold text-gray-800 mb-2">
          Add Your Profile Picture
        </h2>
        <p className="text-gray-500 text-sm mb-6">
          Make your account more personal
        </p>

        {/* ⭕ Circular Upload */}
        <div className="relative w-32 h-32 mx-auto mb-6">
          <label htmlFor="fileInput" className="block cursor-pointer">
            <div className="w-32 h-32 rounded-full bg-gray-200 border-4 border-purple-500 flex items-center justify-center text-4xl text-gray-500 overflow-hidden shadow-md">
              {preview ? (
                <img
                  src={preview}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <span>👤</span>
              )}
            </div>
          </label>

          <label
            htmlFor="fileInput"
            className="absolute bottom-0 right-0 bg-purple-600 text-white p-2 rounded-full shadow cursor-pointer hover:bg-purple-700 transition"
          >
            ➕
          </label>

          <input
            id="fileInput"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              if (!f.type.startsWith("image/"))
                return alert("Please upload an image file.");
              if (f.size > 5 * 1024 * 1024)
                return alert("Image too large. Max 5 MB.");
              setFile(f);
              const url = URL.createObjectURL(f);
              setPreview(url);
            }}
          />
        </div>

        {/* 🔘 Upload Button */}
        <button
          onClick={handleUpload}
          className="w-full py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white font-bold rounded-xl hover:from-purple-600 hover:to-blue-500 transition-all"
        >
          Upload & Continue →
        </button>
      </div>
    </div>
  );
};

export default UploadProfilePicture;
